import React, { useCallback } from 'react';

import { TbCheck, TbBuilding } from 'react-icons/tb';

import { twMerge } from 'tailwind-merge';

import useRedirect from 'hooks/use-redirect';

import Divider from 'components-new/divider/divider';
import Typography from 'components-new/typography/typography';
import Icon from 'components-new/icon/icon';
import Avatar from 'components-new/avatar/avatar';

import { createLocale } from 'locales';

import type { User } from 'state/__generated__/tadaahAPI';

import type { OrganisationType } from 'pages/organization/components/organisation-select/organisation-select';
import organisationRoutes from 'pages/organization/organisation-routes';

import type { AvatarMenuProps } from './avatar-menu/avatar-menu';

interface MobileNavMenuProps {
  user: User | undefined;
  accountMenuItems: AvatarMenuProps['menuItems'];
  accountMenuViews: AvatarMenuProps['views'];
  organisations?: OrganisationType[];
  selectedOrganisation?: OrganisationType;
}

const MobileNavMenu: React.FC<MobileNavMenuProps> = ({
  user,
  accountMenuItems,
  accountMenuViews,
  organisations,
  selectedOrganisation,
}) => {
  const globalLocalize = createLocale('global');
  const redirect = useRedirect();

  const redirectToOrganisationSettings = useCallback(() => {
    redirect(organisationRoutes.settings.path);
  }, [redirect]);

  const redirectToLink = useCallback(
    (link: string) => redirect(link),
    [redirect],
  );
  const selectedView = accountMenuViews.find((view) => view.selected);

  return (
    <div className="flex flex-col md:hidden">
      <Divider orientation="horizontal" className="w-[95%] mx-auto" />
      <div className="flex flex-col p-4">
        {organisations && selectedView?.type === 'organisation' && (
          <>
            <div className="flex flex-row justify-between">
              <Typography
                variant="body-medium"
                as="span"
                className="text-gray-500 uppercase py-2"
              >
                {globalLocalize('organisation')}
              </Typography>

              <Avatar
                image={selectedOrganisation?.image}
                name={selectedOrganisation?.name}
              />
            </div>
            <div>
              {organisations.map((organisation) => (
                <div
                  key={organisation.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => organisation.onClick()}
                  onKeyDown={() => organisation.onClick()}
                  className="rounded hover:bg-gray-200 py-1 flex flex-row items-center"
                >
                  <span className="inline-block w-5 mx-2">
                    {organisation.selected && (
                      <Icon
                        icon={TbCheck}
                        size={20}
                        className="text-primary-300"
                      />
                    )}
                  </span>
                  <Typography
                    as="span"
                    variant="ui-input"
                    className={twMerge(
                      'md:my-0 w-full md:w-auto md:py-0 md:pl-0 font-light',
                      'text-gray-800',
                      organisation.selected && 'text-primary-500',
                    )}
                  >
                    {organisation.name}
                  </Typography>
                </div>
              ))}
              <div className="py-1 my-1 rounded hover:bg-gray-200">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={redirectToOrganisationSettings}
                  onKeyDown={redirectToOrganisationSettings}
                  className="flex flex-row items-center"
                >
                  <Icon
                    icon={TbBuilding}
                    size={20}
                    className="mx-2 text-gray-500"
                  />
                  <Typography variant="ui-input" as="span">
                    {globalLocalize('organisationSettings')}
                  </Typography>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <Divider orientation="horizontal" className="w-[95%] mx-auto" />
      <div className="flex flex-col p-4">
        <div className="flex flex-row justify-between">
          <Typography
            variant="body-medium"
            as="span"
            className="text-gray-500 uppercase py-2"
          >
            {globalLocalize('account')}
          </Typography>
          <Avatar image={user?.picture} name={user?.first_name} />
        </div>
        <div className="mb-2">
          {accountMenuViews.map((view) => (
            <div
              key={view.name}
              role="button"
              tabIndex={0}
              onClick={() => redirectToLink(view.link)}
              onKeyDown={() => redirectToLink(view.link)}
              className="flex flex-row items-center px-2 py-1 rounded hover:bg-gray-200"
            >
              <div className="w-5 mr-2">
                {view.selected && (
                  <Icon icon={TbCheck} size={20} className="text-primary-300" />
                )}
              </div>
              <Typography
                variant="ui-input"
                as="span"
                className={view.selected ? 'text-primary-500' : ''}
              >
                {view.name}
              </Typography>
            </div>
          ))}
        </div>
        <div className="my-2">
          {accountMenuItems.map((item) => (
            <div
              key={item.name}
              role="button"
              tabIndex={0}
              onClick={() => redirectToLink(item.link)}
              onKeyDown={() => redirectToLink(item.link)}
              className="flex flex-row items-center px-2 py-1 rounded hover:bg-gray-200"
            >
              <Icon
                icon={item.icon}
                size={20}
                className="text-gray-500 w-5 mr-2"
              />
              <Typography variant="ui-input" as="span" className="font-light">
                {item.name}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MobileNavMenu;
